import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { httpClient } from "common/httpClient";
import { useMyClaims } from "common/hooks";
import { canExtendSubscription, canPayForSubscription, DEFAULT_EXTENSION_WINDOW_MONTHS } from "./_utils";

/**
 * How far ahead of expiry the logged in user's org can renew a farm. A system admin can widen this
 * per org, usually for a short period while the org lines all of its farms up on one billing cycle.
 *
 * Sourced from useMyClaims rather than the login payload deliberately: the server rebuilds these
 * claims from the org on every request, and useMyClaims refetches on window focus, so a change
 * reaches an already open tab without the user having to log out and back in.
 */
export function useSubscriptionExtensionWindowMonths() {
    const { data } = useMyClaims();

    return data?.subscriptionExtensionWindowMonths ?? DEFAULT_EXTENSION_WINDOW_MONTHS;
}

/** Predicate for "this farm is still running but close enough to expiry to buy another year". */
export function useCanExtendSubscription() {
    const windowMonths = useSubscriptionExtensionWindowMonths();

    return useCallback((farm) => canExtendSubscription(farm, windowMonths), [windowMonths]);
}

/** As above, but also true for farms that have already lapsed. Drives the bulk pay list. */
export function useCanPayForSubscription() {
    const windowMonths = useSubscriptionExtensionWindowMonths();

    return useCallback((farm) => canPayForSubscription(farm, windowMonths), [windowMonths]);
}

/**
 * The farm subscription pricing for the logged in user's org.
 * Returns { listPriceExclGst, discountPercentage, unitPriceExclGst } where unitPriceExclGst is
 * the per farm price after the org's discount has been applied.
 */
export function useFarmSubscriptionPricing() {
    const query = useQuery({
        queryKey: ["payment-details", "farm-subscription-pricing"],
        queryFn: async () => httpClient.get(`payments/unitPrice`),
        retry: false,
        refetchOnWindowFocus: false,
    });

    return {
        isFetching: query.isFetching,
        isLoading: query.isLoading,
        data: query.data,
        error: query.error,
    };
}

export function useMySaveCards() {
    const query = useQuery({
        queryKey: ["payment-details", "saved-cards"],
        queryFn: async () => httpClient.get(`payments/savedCards`),
        retry: false,
        refetchOnWindowFocus: false,
    });

    return {
        isFetching: query.isFetching,
        isLoading: query.isLoading,
        data: query.data,
        error: query.error,
    };
}

export function useMyOrgBalance() {
    const query = useQuery({
        queryKey: ["payment-details", "org-balance"],
        queryFn: async () => httpClient.get(`payments/balance`),
        retry: false,
        refetchOnWindowFocus: false,
    });

    return {
        isFetching: query.isFetching,
        isLoading: query.isLoading,
        data: query.data,
        error: query.error,
    };
}

export async function getPaymentStateAsync(paymentId) {
    try {
        return httpClient.get(`payments/${paymentId}/state`);
    } catch (ex) {
        if (ex.status === 400 && ex?.errors?.length > 0) {
            throw new Error(ex.errors[0].errorMessage);
        } else {
            throw new Error(ex.message);
        }
    }
}

export async function payByInvoiceAsync(farmIds, reference) {
    try {
        const content = { farmIds, reference };
        return httpClient.post(`payments/invoice`, content);
    } catch (ex) {
        if (ex.status === 400 && ex?.errors?.length > 0) {
            throw new Error(ex.errors[0].errorMessage);
        } else {
            throw new Error(ex.message);
        }
    }
}

export async function payByPaystationCardAsync(farmIds, reference, paymentMethod, saveCard) {
    try {
        const content = { farmIds, reference, saveCard, paymentMethod };
        return httpClient.post(`payments/payByPaystationCard`, content);
    } catch (ex) {
        if (ex.status === 400 && ex?.errors?.length > 0) {
            throw new Error(ex.errors[0].errorMessage);
        } else {
            throw new Error(ex.message);
        }
    }
}

export async function addPaystationCardAsync(farmIds, reference, paymentMethod, saveCard) {
    try {
        const content = { farmIds, reference, saveCard, paymentMethod };
        return httpClient.post(`payments/addPaystationCard`, content);
    } catch (ex) {
        if (ex.status === 400 && ex?.errors?.length > 0) {
            throw new Error(ex.errors[0].errorMessage);
        } else {
            throw new Error(ex.message);
        }
    }
}

export async function payByPaystationTokenAsync(farmIds, reference, paymentTokenId) {
    try {
        const content = { farmIds, reference, paymentTokenId };
        return httpClient.post(`payments/payByPaystationToken`, content);
    } catch (ex) {
        if (ex.status === 400 && ex?.errors?.length > 0) {
            throw new Error(ex.errors[0].errorMessage);
        } else {
            throw new Error(ex.message);
        }
    }
}

export async function setRecurringFarmSubscriptionsAsync(farmIds, paymentTokenId) {
    try {
        const content = { farmIds, paymentTokenId };
        return httpClient.post(`payments/recurring`, content);
    } catch (ex) {
        if (ex.status === 400 && ex?.errors?.length > 0) {
            throw new Error(ex.errors[0].errorMessage);
        } else {
            throw new Error(ex.message);
        }
    }
}

export async function sendPaymentAdvice(paymentAdvice) {
    try {
        return httpClient.post(`payments/paymentAdvice`, paymentAdvice);
    } catch (ex) {
        if (ex.status === 400 && ex?.errors?.length > 0) {
            throw new Error(ex.errors[0].errorMessage);
        } else {
            throw new Error(ex.message);
        }
    }
}
